import { useEffect, useState } from "react";
import { api, apiUpload } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, XCircle, KeyRound, AtSign, ImagePlus, Save } from "lucide-react";

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";
const labelCls = "block text-sm font-medium text-cfhtb-heading mb-1.5";

const STATUT_LABELS = {
  sent: "Envoyé", delivered: "Délivré", opened: "Ouvert", clicked: "Cliqué",
  soft_bounce: "Rebond temporaire", hard_bounce: "Rebond définitif",
  blocked: "Bloqué", spam: "Spam", deferred: "Différé", error: "Erreur",
};

function BrevoLogs() {
  const [logs, setLogs] = useState(null);
  const [erreur, setErreur] = useState(null);
  const charger = () => {
    setLogs(null);
    setErreur(null);
    api("/admin/messagerie/brevo/logs?limit=20")
      .then((d) => setLogs(d.items))
      .catch((e) => setErreur(e.message));
  };
  useEffect(charger, []);
  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4" data-testid="brevo-logs-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Journal d'envoi Brevo</h2>
          <p className="text-xs text-cfhtb-text mt-1">
            Les 20 derniers emails transactionnels (campagnes, liens de connexion, accusés de réception…).
          </p>
        </div>
        <button type="button" onClick={charger} data-testid="brevo-logs-refresh"
          className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-3 py-1.5 text-sm font-medium hover:bg-cfhtb-bg transition-colors shrink-0">
          <Mail className="h-4 w-4" /> Actualiser
        </button>
      </div>
      {erreur && <p className="text-sm text-cfhtb-error" data-testid="brevo-logs-error">{erreur}</p>}
      {!logs && !erreur && <p className="text-sm text-cfhtb-text">Chargement…</p>}
      {logs && logs.length === 0 && (
        <p className="text-sm text-cfhtb-text" data-testid="brevo-logs-empty">Aucun email journalisé pour le moment.</p>
      )}
      {logs && logs.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="brevo-logs-table">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cfhtb-text">Date</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cfhtb-text">Destinataire</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cfhtb-text">Objet</th>
                <th className="text-left px-3 py-2 text-xs font-semibold uppercase tracking-wide text-cfhtb-text">Statut</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l, i) => (
                <tr key={l.message_id || i} className="border-b border-slate-50" data-testid={`brevo-log-${i}`}>
                  <td className="px-3 py-2 text-sm text-cfhtb-text whitespace-nowrap">
                    {l.date ? new Date(l.date).toLocaleString("fr-FR") : "—"}
                  </td>
                  <td className="px-3 py-2 text-sm text-cfhtb-heading">{l.email || "—"}</td>
                  <td className="px-3 py-2 text-sm text-cfhtb-heading">{l.sujet || "—"}</td>
                  <td className="px-3 py-2 text-sm">
                    <span className="inline-block text-xs font-medium bg-cfhtb-bg text-cfhtb-primary rounded-full px-2 py-0.5">
                      {STATUT_LABELS[l.statut] || l.statut || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function SignatureStudio() {
  const [signatures, setSignatures] = useState(null);
  const [selId, setSelId] = useState("defaut");
  const [nom, setNom] = useState("");
  const [html, setHtml] = useState("");
  const [busy, setBusy] = useState(false);

  const charger = () => api("/admin/messagerie/signatures")
    .then((d) => setSignatures(d.items)).catch((e) => toast.error(e.message));
  useEffect(() => {
    api("/admin/messagerie/signatures").then((d) => {
      setSignatures(d.items);
      const def = d.items.find((s) => s.id === "defaut");
      if (def) { setNom(def.nom || ""); setHtml(def.texte || ""); }
    }).catch((e) => toast.error(e.message));
  }, []);

  const estDefaut = selId === "defaut";
  const estNouvelle = selId === "__new__";
  const courante = (signatures || []).find((s) => s.id === selId);

  const choisir = (id) => {
    setSelId(id);
    if (id === "__new__") { setNom(""); setHtml(""); return; }
    const s = (signatures || []).find((x) => x.id === id);
    setNom(s?.nom || "");
    setHtml(s?.texte || "");
  };

  const enregistrer = async () => {
    if (estNouvelle && !nom.trim()) { toast.error("Saisissez un nom pour la signature"); return; }
    setBusy(true);
    try {
      if (estNouvelle) {
        await api("/admin/messagerie/signatures", { method: "POST", body: { nom: nom.trim(), texte: html } });
        toast.success("Signature créée");
        const d = await api("/admin/messagerie/signatures");
        setSignatures(d.items);
        const cree = d.items.find((s) => !s.defaut && s.nom === nom.trim());
        setSelId(cree ? cree.id : "defaut");
      } else if (estDefaut) {
        await api("/admin/messagerie/signature", { method: "PUT", body: { texte: html } });
        toast.success("Signature par défaut enregistrée");
        charger();
      } else {
        await api(`/admin/messagerie/signatures/${selId}`, { method: "PUT", body: { nom: nom.trim(), texte: html } });
        toast.success("Signature enregistrée");
        charger();
      }
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const supprimer = async () => {
    if (estDefaut || estNouvelle || !courante) return;
    if (!window.confirm(`Supprimer la signature « ${courante.nom} » ?`)) return;
    try {
      await api(`/admin/messagerie/signatures/${selId}`, { method: "DELETE" });
      toast.success("Signature supprimée");
      choisir("defaut");
      charger();
    } catch (e) { toast.error(e.message); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    const url = estDefaut ? "/admin/messagerie/signature/image"
      : `/admin/messagerie/signatures/${selId}/image`;
    try {
      await apiUpload(url, fd);
      toast.success("Image de la signature mise à jour");
      charger();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5" data-testid="signatures-studio">
      <div>
        <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Signatures des emails</h2>
        <p className="text-xs text-cfhtb-text mt-1">
          La signature par défaut est appliquée à tous les emails automatiques. Les variantes sont
          sélectionnables lors de l'envoi d'une campagne, d'un onboarding ou d'un test.
        </p>
      </div>
      {signatures === null ? (
        <p className="text-sm text-cfhtb-text">Chargement…</p>
      ) : (
        <>
          <div>
            <label className={labelCls}>Signature à modifier</label>
            <select data-testid="signature-select" value={selId} onChange={(e) => choisir(e.target.value)}
              className={inputCls}>
              {signatures.map((s) => (
                <option key={s.id} value={s.id}>{s.defaut ? "Signature par défaut" : s.nom}</option>
              ))}
              <option value="__new__">＋ Nouvelle signature…</option>
            </select>
          </div>
          {!estDefaut && (
            <div>
              <label className={labelCls}>Nom de la signature</label>
              <input data-testid="signature-nom-input" className={inputCls} value={nom}
                onChange={(e) => setNom(e.target.value)} placeholder="Ex. Bureau CFHTB" />
            </div>
          )}
          <div>
            <label className={labelCls}>Contenu (éditeur riche)</label>
            <RichTextEditor key={selId} testid="signature-wysiwyg" minHeight={120}
              content={html} onUpdate={setHtml} />
          </div>
          {!estNouvelle && (
            <div className="space-y-2">
              <label className={labelCls}>Image de signature (logo, bandeau — 2 Mo max)</label>
              {courante?.image_url && (
                <img src={courante.image_url} alt="Aperçu" data-testid="signature-image-preview"
                  className="max-w-[220px] h-auto rounded-md border border-slate-200" />
              )}
              <div>
                <label data-testid="signature-upload-button"
                  className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg cursor-pointer transition-colors">
                  <ImagePlus className="h-4 w-4" /> {courante?.image_url ? "Remplacer l'image" : "Téléverser une image"}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden"
                    data-testid="signature-image-input" onChange={(e) => uploadImage(e.target.files?.[0])} />
                </label>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            {!estDefaut && !estNouvelle && (
              <button type="button" data-testid="signature-delete-button" onClick={supprimer}
                className="inline-flex items-center gap-1.5 bg-white text-cfhtb-error border border-cfhtb-error rounded-md px-3 py-1.5 text-sm font-medium hover:bg-cfhtb-error/5 transition-colors">
                <XCircle className="h-4 w-4" /> Supprimer
              </button>
            )}
            <button type="button" data-testid="signature-save-button" onClick={enregistrer} disabled={busy}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-50 transition-colors">
              <Save className="h-4 w-4" /> {busy ? "Enregistrement…" : estNouvelle ? "Créer la signature" : "Enregistrer"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function MessagerieParametres() {
  const [config, setConfig] = useState(null);
  const [sender, setSender] = useState({ adresse_envoi: "", nom_expediteur: "", contact_destinataire: "" });
  const [savingSender, setSavingSender] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api("/admin/messagerie/config")
      .then((c) => {
        setConfig(c);
        setSender({ adresse_envoi: c.adresse_envoi, nom_expediteur: c.nom_expediteur, contact_destinataire: c.contact_destinataire || "" });
      })
      .catch((e) => toast.error(e.message));
  }, []);

  const saveSender = async (e) => {
    e.preventDefault();
    setSavingSender(true);
    try {
      const res = await api("/admin/messagerie/config", { method: "PUT", body: { ...sender, contact_destinataire: sender.contact_destinataire || null } });
      setConfig((c) => ({ ...c, adresse_envoi: res.adresse_envoi, nom_expediteur: res.nom_expediteur, contact_destinataire: res.contact_destinataire, surcharge: true }));
      toast.success("Expéditeur mis à jour");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingSender(false);
    }
  };

  const sendTest = async (e) => {
    e.preventDefault();
    setTestBusy(true);
    setTestResult(null);
    try {
      const res = await api("/admin/messagerie/test", { method: "POST", body: { email: testEmail } });
      setTestResult(res);
      if (res.envoye) toast.success(`Email de test envoyé à ${res.destinataire}`);
    } catch (err) {
      setTestResult({ envoye: false, detail: err.message });
    } finally {
      setTestBusy(false);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="messagerie-parametres-page" className="max-w-3xl space-y-6 animate-fade-in">
        <div className="flex items-center gap-3">
          <Mail className="h-7 w-7 text-cfhtb-primary" />
          <div>
            <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Messagerie — Paramètres Brevo</h1>
            <p className="text-sm text-cfhtb-text">Configuration de l'envoi et signature appliquée à tous les emails.</p>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5" data-testid="messagerie-config-card">
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Configuration</h2>
          {!config ? (
            <div className="h-6 w-6 border-2 border-cfhtb-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="flex items-start gap-3">
                <AtSign className="h-5 w-5 text-cfhtb-secondary mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-cfhtb-text/70 font-medium">Adresse d'envoi</p>
                  <p className="text-sm font-medium text-cfhtb-heading mt-0.5" data-testid="sender-email-value">{config.adresse_envoi}</p>
                  <p className="text-xs text-cfhtb-text">{config.nom_expediteur}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <KeyRound className="h-5 w-5 text-cfhtb-secondary mt-0.5" />
                <div>
                  <p className="text-xs uppercase tracking-wider text-cfhtb-text/70 font-medium">Clé API Brevo</p>
                  {config.cle_configuree ? (
                    <p className="text-sm font-medium text-cfhtb-success mt-0.5" data-testid="api-key-status">Configurée (…{config.cle_fin})</p>
                  ) : (
                    <p className="text-sm font-medium text-cfhtb-error mt-0.5" data-testid="api-key-status">Non configurée — mode démo</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={saveSender} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4" data-testid="sender-form">
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Expéditeur des emails</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Adresse d'envoi</label>
              <input type="email" required data-testid="sender-email-input" value={sender.adresse_envoi}
                onChange={(e) => setSender({ ...sender, adresse_envoi: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Nom affiché</label>
              <input required data-testid="sender-name-input" value={sender.nom_expediteur}
                onChange={(e) => setSender({ ...sender, nom_expediteur: e.target.value })} className={inputCls} />
            </div>
          </div>
          <div>
            <label className={labelCls}>Adresse de destination du formulaire de contact</label>
            <input type="email" data-testid="contact-dest-input" value={sender.contact_destinataire}
              onChange={(e) => setSender({ ...sender, contact_destinataire: e.target.value })} className={inputCls}
              placeholder="Ex. : contact@cfhtb.org" />
            <p className="text-xs text-cfhtb-text/70 mt-1">
              Les messages envoyés par les adhérents depuis l'espace « Contact » arriveront à cette adresse. Laissez vide pour désactiver le formulaire.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-cfhtb-text/70">
              Valeur par défaut (variable serveur) : {config?.adresse_envoi_env}. L'adresse doit être validée dans Brevo.
            </p>
            <button type="submit" data-testid="sender-save-button" disabled={savingSender}
              className="bg-cfhtb-primary text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60 transition-colors">
              {savingSender ? "Enregistrement…" : "Enregistrer"}
            </button>
          </div>
        </form>

        <SignatureStudio />

        <form onSubmit={sendTest} className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4">
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Envoyer un email de test</h2>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className={labelCls}>Destinataire</label>
              <input type="email" required data-testid="test-email-input" value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)} placeholder="vous@exemple.org" className={inputCls} />
            </div>
            <button type="submit" data-testid="test-send-button" disabled={testBusy || !config?.cle_configuree}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-60 transition-colors">
              <Send className="h-4 w-4" /> {testBusy ? "Envoi…" : "Tester l'envoi"}
            </button>
          </div>
          {testResult && (
            <div data-testid={testResult.envoye ? "test-result-success" : "test-result-error"}
              className={`flex items-start gap-3 rounded-md border p-4 text-sm ${
                testResult.envoye ? "bg-cfhtb-success/5 border-cfhtb-success/30 text-cfhtb-success" : "bg-cfhtb-error/5 border-cfhtb-error/30 text-cfhtb-error"}`}>
              {testResult.envoye ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
              <p className="font-medium">{testResult.envoye ? `Email accepté par Brevo — ${testResult.destinataire}` : `Échec : ${testResult.detail}`}</p>
            </div>
          )}
        </form>

        <BrevoLogs />
      </div>
    </AppLayout>
  );
}
